import React, { useEffect, useRef, useState } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { Environment, useGLTF, ContactShadows } from "@react-three/drei";
import { useSpring } from "@react-spring/core";
import { a as three } from "@react-spring/three";
import { a as web } from "@react-spring/web";
import * as THREE from "three";
import screenImage from "../background.jpeg";

import { useNavigate } from "react-router-dom";

const vec = new THREE.Vector3();

function Model({ open, hinge, scale, ...props }) {
  const group = useRef();

  const { nodes, materials } = useGLTF("/mac-draco.glb");

  const [hovered, setHovered] = useState(false);
  useEffect(() => {
    document.body.style.cursor = hovered ? "pointer" : "auto";
  }, [hovered]);

  useFrame((state) => {
    const t = state.clock.getElapsedTime();
    state.camera.position.lerp(vec.set(0, 0, open ? -24 : -32), 0.1);
    state.camera.lookAt(0, 0, 0);
    group.current.rotation.x = THREE.MathUtils.lerp(
      group.current.rotation.x,
      open ? Math.cos(t / 2) / 8 + 0.25 : Math.cos(t / 2) / 8 + 0.25,
      0.1
    );
    group.current.rotation.y = THREE.MathUtils.lerp(
      group.current.rotation.y,
      open ? Math.sin(t / 4) / 4 : 0,
      0.1
    );
    group.current.rotation.z = THREE.MathUtils.lerp(
      group.current.rotation.z,
      open ? Math.sin(t / 4) / 4 : 0,
      0.1
    );
    group.current.position.y = THREE.MathUtils.lerp(
      group.current.position.y,
      open ? (-2 + Math.sin(t)) / 3 : -4.3,
      0.1
    );
  });

  const texture = new THREE.TextureLoader().load(screenImage);

  // Define a skin material for the metal casing
  const skinMaterial = new THREE.MeshStandardMaterial({
    color: "#B4B4B7", // Adjust the color as needed
    roughness: 0.5, // Adjust as needed
    metalness: 0.5, // Adjust as needed
  });

  return (
    <group
      ref={group}
      scale={[scale, scale, scale]}
      {...props}
      onPointerOver={(e) => (e.stopPropagation(), setHovered(true))}
      onPointerOut={() => setHovered(false)}
      dispose={null}
    >
      <three.group rotation-x={hinge} position={[0, -0.04, 0.41]}>
        <group position={[0, 2.96, -0.13]} rotation={[Math.PI / 2, 0, 0]}>
          {/* Apply the skin material to the metal casing */}
          <mesh material={skinMaterial} geometry={nodes["Cube008"].geometry} />
          <mesh
            material={materials["matte.001"]}
            geometry={nodes["Cube008_1"].geometry}
          />
          <mesh
            material={materials["screen.001"]}
            geometry={nodes["Cube008_2"].geometry}
            scale={[1, 1, 1]}
          >
            <meshBasicMaterial attach="material" map={texture} />
          </mesh>
        </group>
      </three.group>
      <mesh
        material={materials.keys}
        geometry={nodes.keyboard.geometry}
        position={[1.79, 0, 3.45]}
      />
      <group position={[0, -0.1, 3.39]}>
        <mesh
          material={materials.aluminium}
          geometry={nodes["Cube002"].geometry}
        />
        <mesh
          material={materials.trackpad}
          geometry={nodes["Cube002_1"].geometry}
        />
      </group>
      <mesh
        material={materials.touchbar}
        geometry={nodes.touchbar.geometry}
        position={[0, -0.03, 1.2]}
      />
    </group>
  );
}

function Laptop({ onOpenChange, onNextChange }) {
  const [open, setOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 500px)");
    setIsMobile(mediaQuery.matches);

    const handleMediaQueryChange = (event) => {
      setIsMobile(event.matches);
    };

    mediaQuery.addEventListener("change", handleMediaQueryChange);

    return () => {
      mediaQuery.removeEventListener("change", handleMediaQueryChange);
    };
  }, []);

  const props = useSpring({ open: Number(open) });

  return (
    <web.main
      style={{
        background: props.open.to([0, 1], ["#020617", "#030712"]),
        transition: "background 0.5s ease-in",
        width: "100vw",
        height: "100vh",
        overflow: "hidden",
        position: "absolute",
        top: 0,
        left: 0,
        margin: 0,
        padding: 0,
      }}
    >
      <Canvas dpr={[1, 2]} camera={{ position: [0, -2, 0], fov: 35 }}>
        <three.pointLight
          position={[10, 10, 10]}
          intensity={5}
          color={props.open.to([0, 1], ["#f0f0f0", "#1903EF"])}
        />

        <ContactShadows
          rotation-x={Math.PI / 2}
          position={[0, 0, 0]}
          opacity={0.4}
          width={20}
          height={20}
          blur={2}
          far={4.5}
        />
        <group
          rotation={[0, Math.PI, 0]}
          onClick={() => {
            setOpen(!open);
            onOpenChange(!open);
            if (open) {
              onNextChange(true);
            }
          }}
        >
          <Model
            open={open}
            hinge={props.open.to([0, 1], [1.575, -0.425])}
            scale={isMobile ? 0.5 : 0.9}
          />
        </group>
      </Canvas>
    </web.main>
  );
}

export default Laptop;
